import type { RouterConfig } from "nuxt/schema";
import { createMemoryHistory, createWebHashHistory, createWebHistory } from 'vue-router';
import autoLogin from "~/client/middleware/autoLogin";
import eventPageLayout from "~/client/middleware/eventPageLayout";
import setIntro from "~/client/middleware/setIntro";
import authGuard from "~/client/middleware/auth-guard";
import { UserAuthGroup } from "~/enums";
import { useUserStore } from "~/store/userStore";
import { routeToc } from './route-toc';
import routerJson from '@/assets/data/router.json';
import authRouterJson from '@/assets/data/auth_router.json';
import { useRouteStore } from "~/store/routeStore";
import DashboardDefaultPage from '@/client/pages/design-toc/dashboard/default.vue';

// 라우트 컴포넌트 매핑 
const routeComponentMap: { [key: string]: () => Promise<any> } = {
  'dashboard': () => import('@/client/pages/design-toc/dashboard/index.vue'),
  // NOTE: 일부 배포/리버스프록시 환경에서 해당 lazy import fetch가 간헐적으로 실패해
  // 로그인 직후 대시보드 진입이 막히는 현상이 있어 기본 대시보드만 정적 참조로 고정
  'dashboard-index': () => Promise.resolve(DashboardDefaultPage),
  'dashboard-map-info': () => import('@/client/pages/design-toc/dashboard/map-dashboard.vue'),
  'dashboard-site-default-equip-equipid': () => import('@/client/pages/design-toc/sites/equip/detail.vue'),
  'sites': () => import('@/client/pages/design-toc/sites/index.vue'),
  'sites-detail': () => import('@/client/pages/design-toc/sites/index.vue'),
  'sites-detail-default': () => import('@/client/pages/design-toc/sites/detail.vue'),
  'sites-detail-default-equip-equipid': () => import('@/client/pages/design-toc/sites/equip/detail.vue'),
  'sites-detail-siteid': () => import('@/client/pages/design-toc/sites/detail.vue'),
  'sites-detail-siteid-equip-equipid': () => import('@/client/pages/design-toc/sites/equip/detail.vue'),
  'sites-detail-prod-info': () => import('@/client/pages/design-toc/sites/prod-info.vue'),
  'sites-event': () => import('@/client/pages/design-toc/eventinfo/eventlist.vue'),
  'sites-event-event-detail-eventId': () => import('@/client/pages/design-toc/eventinfo/event-detail/[eventId].vue'),
  'design-toc-analysis': () => import('@/client/pages/design-toc/analysis/index.vue'),
  'design-toc-analysis-owner': () => import('@/client/pages/design-toc/analysis/owner.vue'),
  'design-toc-analysis-compare-owner': () => import('@/client/pages/design-toc/analysis/compare-owner.vue'),
  'design-toc-analysis-compare-region': () => import('@/client/pages/design-toc/analysis/compare-region.vue'),
  'asinfo': () => import('@/client/pages/design-toc/asinfo/index.vue'),
  'asinfo-aslist': () => import('@/client/pages/design-toc/asinfo/aslist.vue'),
  'asinfo-aslist-as-request-cid': () => import('@/client/pages/design-toc/asinfo/as-request.vue'),
  'asinfo-as-request-list': () => import('@/client/pages/design-toc/asinfo/as-request-list.vue'),
  'asinfo-as-request-list-as-request-detail-asMngSeq': () => import('@/client/pages/design-toc/asinfo/as-request-detail.vue'),
  'bpm': () => import('@/client/pages/design-toc/bpm/index.vue'),
  'bpm-bpmlist': () => import('@/client/pages/design-toc/bpm/bpmlist.vue'),
  'bpm-bpm-request': () => import('@/client/pages/design-toc/bpmrequestinfo/bpmrequests.vue'),
  'bpm-cost-estimate': () => import('@/client/pages/design-toc/bpmrequestinfo/cost-estimate.vue'),
  'bpm-cost-estimate-by-company': () => import('@/client/pages/design-toc/bpmrequestinfo/cost-estimate-by-company.vue'),
  'customer': () => import('@/client/pages/design-toc/customer/index.vue'),
  'customer-notice': () => import('@/client/pages/design-toc/customer/notice.vue'),
  'customer-notice-detail': () => import('@/client/pages/design-toc/customer/notice-detail.vue'),
  'customer-qna': () => import('@/client/pages/design-toc/customer/qna.vue'),
  'customer-qna-qna-detail': () => import('@/client/pages/design-toc/customer/qna-detail.vue'),
  'customer-qna-qna-write': () => import('@/client/pages/design-toc/customer/qna-write.vue'),
  'user-service': () => import('@/client/pages/design-toc/user-service/index.vue'),
  'user-privacy-service': () => import('@/client/pages/design-toc/user-service/index.vue'),
  'user-service-user-info': () => import('@/client/pages/design-toc/user-service/user-info.vue'),
  'user-privacy-service-user-info': () => import('@/client/pages/design-toc/user-service/user-info.vue'),
  'user-service-user-setting': () => import('@/client/pages/design-toc/user-service/user-setting.vue'),
  'user-service-manager-info': () => import('@/client/pages/design-toc/user-service/manager-info.vue'),
  'user-service-set-manager': () => import('@/client/pages/design-toc/user-service/set-manager.vue'),
  'management': () => import('@/client/pages/design-toc/management/index.vue'),
  'management-installer': () => import('@/client/pages/design-toc/management/installer.vue'),
  'management-as-equip-info': () => import('@/client/pages/design-toc/management/as-equip-info.vue'),
  'management-as-owner-info': () => import('@/client/pages/design-toc/management/as-owner-info.vue'),
  'management-rtu-installer': () => import('@/client/pages/design-toc/management/rtu-installer.vue'),
  'management-rtu-install-report': () => import('@/client/pages/design-toc/management/rtu-install-report.vue'),
  'management-rtu-info': () => import('@/client/pages/design-toc/management/rtu-info.vue'),
  'terms': () => import('@/client/pages/design-toc/terms/index.vue'),
  'terms-list': () => import('@/client/pages/design-toc/terms/terms.vue'),
  'map-info': () => import('@/client/pages/design-toc/map/index.vue'),
  'dashboard-map': () => import('@/client/pages/design-toc/dashboard/equip-map.vue'),
  'dashboard-map-equip-detail-cid-pv': () => import('@/client/pages/toc/equip/[pathinfo]/equip-detail/[cid]/pv.vue'),
  'dashboard-map-equip-detail-cid-geo': () => import('@/client/pages/toc/equip/[pathinfo]/equip-detail/[cid]/geo.vue'),
  'dashboard-map-equip-detail-cid-solarheat': () => import('@/client/pages/toc/equip/[pathinfo]/equip-detail/[cid]/solarheat.vue'),
}

// allReplaceComponent 함수를 외부에서 사용할 수 있도록 export
export const allReplaceComponent = (page: any) => {
  if (page.children && page.children.length > 0) {
    page.children.forEach((child: any) => {
      allReplaceComponent(child);
    })
  }
  if (typeof page.component === 'string') {
    page.component = routeComponentMap[page.name];
  }
}

// auth_router.json의 타입 정의
interface AuthRouterConfig {
  userSeq: number,
  routes: [{
    parent: string,
    children: string[]
  }] 
}

// userRouteList의 타입 정의
interface UserRouteList {
  userSeq: number,
  routes: [{
    parent: string,
    children: string[]
  }] 
}

// auth_router.json에서 계정별 라우트 권한을 가져오는 함수
// TODO: API 기능 완성 후 활성화 예정
// const getAuthRouterConfig = (accountName: string): UserRouteList | null => {
//   const routeStore = useRouteStore();
//   const userRouteList = routeStore.userRouteList;
//
//   console.log('userRouteList', userRouteList);
//   
//   // userRouteList가 배열인 경우 첫 번째 요소를 사용, 객체인 경우 그대로 사용
//   const routeConfig = Array.isArray(userRouteList) ? userRouteList[0] : userRouteList;
//   
//   if(routeConfig && routeConfig?.routes?.length > 0) {
//     return routeConfig as UserRouteList;
//   }
//
//   return null;
// }

// getDesignTocRoute 함수를 외부에서 사용할 수 있도록 export
export const getDesignTocRoute = (userAuthGroup: number) => {
  // userStore를 함수 내부에서 가져와서 최신 상태를 반영하도록 수정
  const userStore = useUserStore();
  const userRoutePermission = userStore.userRoutePermission;
  // 사용자 ID나 다른 고유 식별자를 사용하여 계정별 라우트 권한 관리
  // TODO: API 기능 완성 후 활성화 예정
  // const userSeq = userStore.loginUserInfo?.userSeq;
  // const accountName = userSeq ? `${userSeq}` : null; // userSeq 기반 계정명 생성

  const isAdmin = userStore.loginUserInfo?.isCompAdminManager;

  // auth_router.json에서 해당 계정의 라우트 권한 확인
  // TODO: API 기능 완성 후 활성화 예정 - 현재는 router.json만 사용
  // const authRouterConfig = accountName ? getAuthRouterConfig(accountName) : null;
  const authRouterConfig = null; // router.json만 사용
  // console.log('Auth Router Config for account:', accountName, authRouterConfig);

  let form: any = {
    name: 'design-toc',
    meta: { layout: 'toc-design' as const, name: 'design-toc', middleware: [authGuard] },
    path: 'toc',
    component: () => import('@/client/pages/design-toc/index.vue'),
    children: [
      {
        name: 'design-toc-user',
        path: 'user',
        meta: { name: '사용자', sidebar: false },
        component: () => import('@/client/pages/design-toc/user/index.vue'),
        children: [
          {
            name: 'design-toc-user-login',
            path: 'login',
            meta: { name: '로그인' },
            component: () => import('@/client/pages/design-toc/user/login.vue')
          }
        ]
      }
    ]
  }

  const routeNameAliasMap: Record<string, string> = {
    'dashboard-site-siteid-equip-equipid': 'dashboard-site-default-equip-equipid',
    'sites-detail-siteid-equip-equipid': 'sites-detail-default-equip-equipid',
  };
  const normalizeRouteName = (name: string) => routeNameAliasMap[name] ?? name;

  for (let i = 0; i < routerJson.length; i++) {
    // 1. 가장 먼저 router.json의 메인 메뉴 userAuthGroup 체크 (모든 경우에 공통)
    if (!routerJson[i].userAuthGroup || !Array.isArray(routerJson[i].userAuthGroup) || !routerJson[i].userAuthGroup.includes(userAuthGroup)) {
      // console.log('Main menu filtered by userAuthGroup:', routerJson[i].key, 'userAuthGroup:', routerJson[i].userAuthGroup, 'current:', userAuthGroup);
      continue;
    }

    // 2. isAdmin 체크 (isAdmin이 true인 경우 isAdmin === '1'이어야 함)
    if (routerJson[i].isAdmin === true) {
      if (isAdmin !== '1') {
        // console.log('Menu filtered by isAdmin:', routerJson[i].key, 'isAdmin required but user isAdmin:', isAdmin);
        continue;
      }
    }

    // 3. authRouterConfig가 있는 경우와 없는 경우로 분기
    // TODO: API 기능 완성 후 활성화 예정 - 현재는 router.json만 사용
    // const allowedRoute = authRouterConfig && authRouterConfig.routes 
    //   ? authRouterConfig.routes.find((route: any) => route.parent === routerJson[i].key)
    //   : null;
    const allowedRoute = null; // router.json만 사용

    // 4. page 객체 생성
    let page: any = {
      name: routerJson[i].name,
      path: routerJson[i].path,
      meta: routerJson[i].meta,
      icon: routerJson[i].icon,
      hidden: routerJson[i].hidden,
      component: routerJson[i].component,
      children: []
    };

    // 5. route 배열 처리
    if (routerJson[i].route && Array.isArray(routerJson[i].route)) {
      routerJson[i].route.forEach((inRoute: any) => {
        // 5-1. 각 route의 userAuthGroup 체크 (필수)
        // 단, Owner인 경우 sites-detail은 userRoutePermission에 따라 처리하므로 여기서는 건너뜀
        const isOwnerSitesDetail = (userAuthGroup === UserAuthGroup.Owner && routerJson[i].key === 'sites' && inRoute.name === 'sites-detail');
        
        if (!isOwnerSitesDetail) {
          // sites-detail이 아닌 경우 또는 Owner가 아닌 경우 일반 userAuthGroup 체크
          if (!inRoute.userAuthGroup || !Array.isArray(inRoute.userAuthGroup) || !inRoute.userAuthGroup.includes(userAuthGroup)) {
            // console.log('Route child filtered by userAuthGroup:', inRoute.name, 'userAuthGroup:', inRoute.userAuthGroup, 'current:', userAuthGroup);
            return; // forEach 내부이므로 return 사용
          }
        }

        // 5-2. dashboard 특별 처리
        if (routerJson[i].key === 'dashboard') {
          if (userAuthGroup === UserAuthGroup.Owner) {
            // Owner인 경우 userRoutePermission에 따른 처리
            const permissionRoutes = (inRoute as any)[userRoutePermission];
            if (permissionRoutes && Array.isArray(permissionRoutes)) {
              // siteone일 때 sites-detail-default는 dashboard의 children으로 표시되어야 함
              // TODO: API 기능 완성 후 활성화 예정 - 현재는 router.json만 사용
              // if (allowedRoute && allowedRoute.children && allowedRoute.children.length > 0) {
              //   // authRouterConfig가 있고 children이 정의된 경우 필터링
              //   const allowedChildren = allowedRoute.children.map((child: string) => normalizeRouteName(child));
              //   const filteredRoutes = permissionRoutes.filter((route: any) => 
              //     allowedChildren.includes(route.name)
              //   );
              //   page.children = filteredRoutes.length > 0 ? filteredRoutes : permissionRoutes;
              // } else {
              //   // authRouterConfig가 없거나 children이 없는 경우 전체 사용
              //   page.children = permissionRoutes;
              // }
              page.children = permissionRoutes;
            }
          } else {
            // Owner가 아닌 경우 default 사용
            if (inRoute.default && Array.isArray(inRoute.default)) {
              // TODO: API 기능 완성 후 활성화 예정 - 현재는 router.json만 사용
              // if (allowedRoute && allowedRoute.children && allowedRoute.children.length > 0) {
              //   // authRouterConfig가 있고 children이 정의된 경우 필터링
              //   const allowedChildren = allowedRoute.children.map((child: string) => normalizeRouteName(child));
              //   const filteredRoutes = inRoute.default.filter((route: any) => 
              //     allowedChildren.includes(route.name)
              //   );
              //   page.children = filteredRoutes.length > 0 ? filteredRoutes : inRoute.default;
              // } else {
              //   // authRouterConfig가 없거나 children이 없는 경우 전체 사용
              //   page.children = inRoute.default;
              // }
              page.children = inRoute.default;
            }
          }
        } 
        // 5-3. sites 특별 처리
        else if (routerJson[i].key === 'sites') {
          if (inRoute.name === 'sites-detail') {
            // sites-detail에 대한 특별한 권한 처리
            if (userAuthGroup !== UserAuthGroup.Owner) {
              // Owner가 아닌 경우 일반 userAuthGroup 체크만 수행 (이미 위에서 체크함)
              page.children.push(inRoute as any);
            } else {
              // Owner인 경우 userRoutePermission에 따른 처리
              // siteone (사이트 1개)인 경우: 설비 목록 메뉴를 보여주지 않음 (dashboard에서 사이트 대시보드 표시)
              // siteover (사이트 여러 개)인 경우: 설비 목록 메뉴 필요
              // equipone (설비 1개)인 경우: 설비 목록 메뉴 불필요
              if (userRoutePermission === 'siteone') {
                // siteone일 때는 설비 목록 메뉴를 보여주지 않음 (dashboard에서 사이트 대시보드 표시)
                // console.log('Skipping sites-detail for siteone (showing in dashboard instead)');
              } else if (userRoutePermission === 'siteover') {
                // siteover일 때는 설비 목록 메뉴 표시 (userAuthGroup에 Owner 추가)
                const routeCopy = { ...inRoute };
                if (!routeCopy.userAuthGroup.includes(UserAuthGroup.Owner)) {
                  routeCopy.userAuthGroup = [...routeCopy.userAuthGroup, UserAuthGroup.Owner];
                }
                // console.log('Adding sites-detail for siteover:', routeCopy.name, 'userAuthGroup:', routeCopy.userAuthGroup);
                page.children.push(routeCopy as any);
              } else if (userRoutePermission === 'equipone') {
                // 설비 1개인 경우 설비 목록 메뉴 불필요 (userAuthGroup에서 Owner 제거)
                // equipone인 경우 sites-detail을 표시하지 않음
                // console.log('Skipping sites-detail for equipone');
              } else {
                // userRoutePermission이 설정되지 않은 경우 기본 동작
                // router.json의 userAuthGroup 체크만 수행
                if (inRoute.userAuthGroup.includes(userAuthGroup)) {
                  page.children.push(inRoute as any);
                }
              }
            }
          } else {
            // sites-detail이 아닌 경우 (sites-detail-prod-info, sites-event 등)
            // userAuthGroup 체크는 이미 위에서 완료했으므로 바로 추가
            // TODO: API 기능 완성 후 활성화 예정 - 현재는 router.json만 사용
            // if (allowedRoute && allowedRoute.children && allowedRoute.children.length > 0) {
            //   // authRouterConfig가 있고 children이 정의된 경우, 포함되어 있으면 추가
            //   const allowedChildren = allowedRoute.children.map((child: string) => normalizeRouteName(child));
            //   if (inRoute.name && allowedChildren.includes(normalizeRouteName(inRoute.name))) {
            //     page.children.push(inRoute as any);
            //   } else {
            //     // allowedRoute.children에 없어도 userAuthGroup이 맞으면 추가 (router.json 우선)
            //     console.log('Route added by userAuthGroup (not in allowedRoute.children):', inRoute.name);
            //     page.children.push(inRoute as any);
            //   }
            // } else {
            //   // authRouterConfig가 없거나 children이 없는 경우 userAuthGroup 체크만으로 필터링 (이미 위에서 체크함)
            //   page.children.push(inRoute as any);
            // }
            page.children.push(inRoute as any);
          }
        } 
        // 5-4. 기타 메뉴 처리
        else {
          // userAuthGroup 체크는 이미 위에서 완료했으므로 바로 추가
          // TODO: API 기능 완성 후 활성화 예정 - 현재는 router.json만 사용
          // if (allowedRoute && allowedRoute.children && allowedRoute.children.length > 0) {
          //   // authRouterConfig가 있고 children이 정의된 경우, 포함되어 있으면 추가
          //   const allowedChildren = allowedRoute.children.map((child: string) => normalizeRouteName(child));
          //   if (inRoute.name && allowedChildren.includes(normalizeRouteName(inRoute.name))) {
          //     page.children.push(inRoute as any);
          //   } else {
          //     // allowedRoute.children에 없어도 userAuthGroup이 맞으면 추가 (router.json 우선)
          //     console.log('Route added by userAuthGroup (not in allowedRoute.children):', inRoute.name);
          //     page.children.push(inRoute as any);
          //   }
          // } else {
          //   // authRouterConfig가 없거나 children이 없는 경우 userAuthGroup 체크만으로 필터링 (이미 위에서 체크함)
          //   page.children.push(inRoute as any);
          // }
          page.children.push(inRoute as any);
        }
      });
    }

    // 6. children이 있는 경우에만 메뉴 추가
    if (page.children.length > 0 || routerJson[i].key === 'dashboard') {
      allReplaceComponent(page);
      form.children.push(page as any);
    } else {
      console.log('Menu skipped due to empty children:', routerJson[i].key);
    }
  }

  return form;
}


export default <RouterConfig>{
  mode: 'history',
  history: createWebHistory,
  routes: (_routes) => {
    const newBaseRoute = () => {
      // userStore를 함수 내부에서 가져와서 최신 상태를 반영하도록 수정
      const userStore = useUserStore();
      const userAuthGroup = userStore.loginUserInfo?.authGroupSeq;


      return [
        {
          name: 'page', path: '/', component: () => import('@/client/pages/index.vue'), meta: { middleware: [autoLogin] }, children: [
            /** SWAGGER */
            process.env.NODE_ENV === 'development' ?
              { name: 'swagger', path: 'swagger-ui', component: () => import("@/client/pages/swagger-ui.vue") }
              : { name: 'swagger', path: '' },
            /** MOBILE */
            getMobileRoutes(),
            getDesignTocRoute(userAuthGroup as number),
            {name: 'toc-pathMatch', meta:{owner:false, manager:false, admin:false}, path:'/:pathMatch(.*)*', redirect: '/toc/user/login'},
            { name: 'main-test', path: '/test/main', component: () => import('@/client/pages/test/main.vue') },
            { name: 'test-qrtest', path: '/test/qrtest', component: () => import('@/client/pages/test/qrtest.vue') },
            { name: 'test-geo', path: '/test/geo', component: () => import('@/client/pages/test/geo.vue') }
          ]
        },
      ]
    }

    // 페이지별 라우트 함수들
    // Mobile 라우트 (Owner, Contractor, Worker)
    const getMobileRoutes = () => {
      /** MOBILE */
      return {
        name: 'mobile', path: 'mobile', meta: { layout: 'mobile' }, component: () => import('@/client/pages/mobile.vue'), children: [
          {
            name: 'mobile-user', path: 'user', meta: { layout: 'non-header-footer' }, component: () => import(`@/client/pages/mobile/user.vue`), children: [
              { name: 'mobile-user-id-index', path: ':id/index', component: () => import('@/client/pages/mobile/user/[id]/index.vue') },
              {
                name: 'mobile-user-id', path: ':id', component: () => import('@/client/pages/mobile/user/[id].vue'), children: [
                  { name: 'mobile-user-id-logout', path: 'logout', component: () => import('@/client/pages/mobile/user/logout.vue') },
                  { name: 'mobile-user-id-equipMap', path: 'equipMap', meta: { layout: 'non-header-footer' }, component: () => import('@/client/pages/mobile/user/[id]/equipMap.vue') },
                  { name: 'mobile-user-id-equip-owner-list', path: 'equipOwnerList', meta: { layout: 'non-header-footer' }, component: () => import('@/client/pages/mobile/user/[id]/equip-owner-list.vue') },
                  { name: 'mobile-user-id-equipList', path: 'equipList', meta: { layout: 'non-header-footer' }, component: () => import('@/client/pages/mobile/user/[id]/equipList.vue') },
                  {
                    name: 'mobile-user-id-views', path: 'views', meta: { layout: 'mobile' }, component: () => import('@/client/pages/mobile/user/[id]/views.vue'), children: [
                      {
                        name: 'mobile-user-id-views-monitoring', path: 'monitoring', component: () => import('@/client/pages/mobile/user/[id]/views/monitoring.vue'), children: [
                          { name: 'mobile-user-id-views-monitoring-pv', path: 'pv', meta: { keepalive: true }, component: () => import('@/client/pages/mobile/user/[id]/views/monitoring/pv.vue') },
                          { name: 'mobile-user-id-views-monitoring-solarheat', path: 'solarheat', component: () => import('@/client/pages/mobile/user/[id]/views/monitoring/solarheat.vue') },
                          { name: 'mobile-user-id-views-monitoring-geo', path: 'geo', component: () => import('@/client/pages/mobile/user/[id]/views/monitoring/geo.vue') },
                          { name: 'mobile-user-id-views-monitoring-wf', path: 'wf', component: () => import('@/client/pages/mobile/user/[id]/views/monitoring/wf.vue') },
                          { name: 'mobile-user-id-views-monitoring-fuelcell', path: 'fuelcell', component: () => import('@/client/pages/mobile/user/[id]/views/monitoring/fuelcell.vue') },
                          { name: 'mobile-user-id-views-monitoring-ess', path: 'ess', component: () => import('@/client/pages/mobile/user/[id]/views/monitoring/ess.vue') }
                        ]
                      },
                      {
                        name: 'mobile-user-id-views-equipInfo', path: 'equipInfo', component: () => import('@/client/pages/mobile/user/[id]/views/equipInfo.vue'), children: [
                          {
                            name: 'mobile-user-id-views-equipInfo-owner-pv', path: 'owner/pv', component: () => import('@/client/pages/mobile/user/[id]/views/equipInfo/owner/pv.vue'), children: [
                              { name: 'mobile-user-id-views-equipInfo-owner-pv-instruinfo', path: 'InstruInfo', component: () => import('@/client/pages/mobile/user/[id]/views/equipInfo/owner/pv/InstruInfo.vue') },
                              { name: 'mobile-user-id-views-equipInfo-owner-pv-installinfo', path: 'InstallInfo', component: () => import('@/client/pages/mobile/user/[id]/views/equipInfo/owner/pv/InstallInfo.vue') }
                            ]
                          },
                          {
                            name: 'mobile-user-id-views-equipInfo-owner-solarheat', path: 'owner/solarheat', component: () => import('@/client/pages/mobile/user/[id]/views/equipInfo/owner/solarheat.vue'), children: [
                              { name: 'mobile-user-id-views-equipInfo-owner-solarheat-instruinfo', path: 'InstruInfo', component: () => import('@/client/pages/mobile/user/[id]/views/equipInfo/owner/solarheat/InstruInfo.vue') },
                              { name: 'mobile-user-id-views-equipInfo-owner-solarheat-installinfo', path: 'InstallInfo', component: () => import('@/client/pages/mobile/user/[id]/views/equipInfo/owner/solarheat/InstallInfo.vue') }
                            ]
                          },
                          {
                            name: 'mobile-user-id-views-equipInfo-owner-geo', path: 'owner/geo', component: () => import('@/client/pages/mobile/user/[id]/views/equipInfo/owner/geo.vue'), children: [
                              { name: 'mobile-user-id-views-equipInfo-owner-geo-instruinfo', path: 'InstruInfo', component: () => import('@/client/pages/mobile/user/[id]/views/equipInfo/owner/geo/InstruInfo.vue') },
                              { name: 'mobile-user-id-views-equipInfo-owner-geo-installinfo', path: 'InstallInfo', component: () => import('@/client/pages/mobile/user/[id]/views/equipInfo/owner/geo/InstallInfo.vue') }
                            ]
                          },
                          { name: 'mobile-user-id-views-equipInfo-owner-wf', path: 'owner/wf', component: () => import('@/client/pages/mobile/user/[id]/views/equipInfo/owner/wf.vue') },
                          { name: 'mobile-user-id-views-equipInfo-owner-fuelcell', path: 'owner/fuelcell', component: () => import('@/client/pages/mobile/user/[id]/views/equipInfo/owner/fuelcell.vue') },
                          { name: 'mobile-user-id-views-equipInfo-owner-ess', path: 'owner/ess', component: () => import('@/client/pages/mobile/user/[id]/views/equipInfo/owner/ess.vue') },

                          {
                            name: 'mobile-user-id-views-equipInfo-company-pv', path: 'company/pv', component: () => import('@/client/pages/mobile/user/[id]/views/equipInfo/company/pv.vue'), children: [
                              { name: 'mobile-user-id-views-equipInfo-company-pv-instruinfo', path: 'InstruInfo', component: () => import('@/client/pages/mobile/user/[id]/views/equipInfo/company/pv/InstruInfo.vue') },
                              { name: 'mobile-user-id-views-equipInfo-company-pv-installinfo', path: 'InstallInfo', component: () => import('@/client/pages/mobile/user/[id]/views/equipInfo/company/pv/InstallInfo.vue') },
                              { name: 'mobile-user-id-views-equipInfo-company-pv-specinfo', path: 'SpecInfo', component: () => import('@/client/pages/mobile/user/[id]/views/equipInfo/company/pv/SpecInfo.vue') },
                            ]
                          },
                          {
                            name: 'mobile-user-id-views-equipInfo-company-solarheat', path: 'company/solarheat', component: () => import('@/client/pages/mobile/user/[id]/views/equipInfo/company/solarheat.vue'), children: [
                              { name: 'mobile-user-id-views-equipInfo-company-solarheat-instruinfo', path: 'InstruInfo', component: () => import('@/client/pages/mobile/user/[id]/views/equipInfo/company/solarheat/InstruInfo.vue') },
                              { name: 'mobile-user-id-views-equipInfo-company-solarheat-specinfo', path: 'SpecInfo', component: () => import('@/client/pages/mobile/user/[id]/views/equipInfo/company/solarheat/SpecInfo.vue') },
                              { name: 'mobile-user-id-views-equipInfo-company-solarheat-installinfo', path: 'InstallInfo', component: () => import('@/client/pages/mobile/user/[id]/views/equipInfo/company/solarheat/InstallInfo.vue') },
                            ]
                          },
                          {
                            name: 'mobile-user-id-views-equipInfo-company-geo', path: 'company/geo', component: () => import('@/client/pages/mobile/user/[id]/views/equipInfo/company/geo.vue'), children: [
                              { name: 'mobile-user-id-views-equipInfo-company-geo-instruinfo', path: 'InstruInfo', component: () => import('@/client/pages/mobile/user/[id]/views/equipInfo/company/geo/InstruInfo.vue') },
                              { name: 'mobile-user-id-views-equipInfo-company-geo-installinfo', path: 'InstallInfo', component: () => import('@/client/pages/mobile/user/[id]/views/equipInfo/company/geo/InstallInfo.vue') },
                              { name: 'mobile-user-id-views-equipInfo-company-geo-specinfo', path: 'SpecInfo', component: () => import('@/client/pages/mobile/user/[id]/views/equipInfo/company/geo/SpecInfo.vue') },
                            ]
                          },
                        ]
                      },
                      {
                        name: 'mobile-user-id-views-powerGeneration', path: 'powerGeneration', component: () => import('@/client/pages/mobile/user/[id]/views/powerGeneration.vue'), children: [
                          { name: 'mobile-user-id-views-powerGeneration-pv', path: 'pv', component: () => import('@/client/pages/mobile/user/[id]/views/powerGeneration/pv.vue') },
                          { name: 'mobile-user-id-views-powerGeneration-solarheat', path: 'solarheat', component: () => import('@/client/pages/mobile/user/[id]/views/powerGeneration/solarheat.vue') },
                          { name: 'mobile-user-id-views-powerGeneration-geo', path: 'geo', component: () => import('@/client/pages/mobile/user/[id]/views/powerGeneration/geo.vue') },
                        ]
                      },
                      {
                        name: 'mobile-user-id-views-report', path: 'report', component: () => import('@/client/pages/mobile/user/[id]/views/report.vue'), children: [
                          { name: 'mobile-user-id-views-report-pv', path: 'pv', component: () => import('@/client/pages/mobile/user/[id]/views/report/pv.vue') },
                          { name: 'mobile-user-id-views-report-geo', path: 'geo', component: () => import('@/client/pages/mobile/user/[id]/views/report/geo.vue') },
                          { name: 'mobile-user-id-views-report-solarheat', path: 'solarheat', component: () => import('@/client/pages/mobile/user/[id]/views/report/solarheat.vue') },
                        ]
                      },
                      {
                        name: 'mobile-user-id-views-more', path: 'more', meta: { layout: 'non-header-footer' }, component: () => import('@/client/pages/mobile/user/[id]/views/more.vue'), children: [
                          { name: 'mobile-user-id-views-more-installinfo', path: 'InstallInfo', component: () => import('@/client/pages/mobile/user/[id]/views/more/InstallInfo.vue') },
                          { name: 'mobile-user-id-views-more-menu', path: 'menu', component: () => import('@/client/pages/mobile/user/[id]/views/more/menu.vue') },
                          { name: 'mobile-user-id-views-more-ownerinfo', path: 'ownerInfo', component: () => import('@/client/pages/mobile/user/[id]/views/more/ownerInfo.vue') },
                          { name: 'mobile-user-id-views-more-predictedPower', path: 'predictedPower', component: () => import('@/client/pages/mobile/user/[id]/views/more/predictedPower.vue') },
                          { name: 'mobile-user-id-views-more-menuToNotice-noticeid', path: 'menuToNotice/:noticeid', component: () => import('@/client/pages/mobile/user/[id]/views/more/menuToNotice/[noticeid].vue') },
                          {
                            name: 'mobile-user-id-views-more-installer-confirm', path: 'installer-confirm', component: () => import('@/client/pages/mobile/user/[id]/views/more/installerConfirm/index.vue'), children: [
                              { name: 'mobile-user-id-views-more-installer-confirm-list', path: 'list', component: () => import('@/client/pages/mobile/user/[id]/views/more/installerConfirm/installerConfirm.vue') },
                              { name: 'mobile-user-id-views-more-installer-confirm-detail', path: 'detail', component: () => import('@/client/pages/mobile/user/[id]/views/more/installerConfirm/installerConfirmDetail.vue') },
                              { name: 'mobile-user-id-views-more-installer-confirm-chgowner', path: 'chg-owner', meta: { titleName: '변경 설치자 선택' }, component: () => import('@/client/pages/mobile/user/[id]/views/more/installerConfirm/changeOwnerList.vue') },
                              { name: 'mobile-user-id-views-more-installer-confirm-history', path: 'history', component: () => import('@/client/pages/mobile/user/[id]/views/more/installerConfirm/installerConfirmHistory.vue') }
                            ]
                          }
                        ]
                      },
                      {
                        name: 'mobile-user-id-views-event', path: 'event', meta: { middleware: [eventPageLayout], layout: 'non-header-footer' }, component: () => import('@/client/pages/mobile/user/[id]/views/event.vue'), children: [
                          { name: 'mobile-user-id-views-event-all', path: 'all', component: () => import('@/client/pages/mobile/user/[id]/views/event/all.vue') },
                          { name: 'mobile-user-id-views-event-rtuevent', path: 'rtuEvent', component: () => import('@/client/pages/mobile/user/[id]/views/event/rtuEvent.vue') },
                          { name: 'mobile-user-id-views-event-eventHistory', path: 'eventHistory', component: () => import('@/client/pages/mobile/user/[id]/views/event/eventHistory.vue') }
                        ]
                      },

                      { name: 'mobile-user-id-views-status', path: 'status', meta: { layout: 'non-header-footer' }, component: () => import('@/client/pages/mobile/user/[id]/views/status.vue') }
                    ]
                  },
                  {
                    name: 'mobile-user-id-etc', path: 'etc', meta: { layout: 'non-header-footer' }, component: () => import('@/client/pages/mobile/user/[id]/etc.vue'), children: [
                      {
                        name: 'mobile-user-id-etc-notice', path: 'notice', component: () => import('@/client/pages/mobile/user/[id]/etc/notice.vue'), children: [
                          { name: 'mobile-user-id-etc-notice-index', path: 'index', meta: { name: '공지사항' }, component: () => import('@/client/pages/mobile/user/[id]/etc/notice/index.vue') },
                          { name: 'mobile-user-id-etc-notice-noticeid', path: ":noticeid", meta: { name: '공지사항' }, component: () => import('@/client/pages/mobile/user/[id]/etc/notice/[noticeid].vue') }
                        ]
                      },
                      {
                        name: 'mobile-user-id-etc-alarm', path: 'alarm', component: () => import('@/client/pages/mobile/user/[id]/etc/alarm.vue'), children: [
                          { name: 'mobile-user-id-etc-alarm-all', path: 'all', meta: { name: '알람' }, component: () => import('@/client/pages/mobile/user/[id]/etc/alarm/all.vue') },
                          { name: 'mobile-user-id-etc-alarm-alarmRTU', path: 'alarmRTU', meta: { name: '알람' }, component: () => import('@/client/pages/mobile/user/[id]/etc/alarm/alarmRTU.vue') },
                          { name: 'mobile-user-id-etc-alarm-alarmETC', path: 'alarmETC', meta: { name: '알람' }, component: () => import('@/client/pages/mobile/user/[id]/etc/alarm/alarmETC.vue') }
                        ]
                      }
                    ]
                  },
                  {
                    name: 'mobile-user-id-cs', path: 'cs', meta: { layout: 'non-header-footer' }, component: () => import('@/client/pages/mobile/user/[id]/cs.vue'), children: [
                      { name: 'mobile-user-id-main', path: 'main', component: () => import('@/client/pages/mobile/user/[id]/cs/main.vue') },
                      { name: 'mobile-user-id-question', path: 'question', component: () => import('@/client/pages/mobile/user/[id]/cs/question.vue') },
                      { name: 'mobile-user-id-useTerms', path: 'useTerms', component: () => import('@/client/pages/mobile/user/[id]/cs/useTerms.vue') },
                      { name: 'mobile-user-id-personTerms', path: 'personTerms', component: () => import('@/client/pages/mobile/user/[id]/cs/personTerms.vue') },
                      { name: 'mobile-user-id-locationTerms', path: 'locationTerms', component: () => import('@/client/pages/mobile/user/[id]/cs/locationTerms.vue') },
                      {
                        name: 'mobile-user-id-oldTerms', path: 'oldTerms', component: () => import('@/client/pages/mobile/user/[id]/cs/oldTerms.vue'), children: [
                          {
                            name: 'mobile-user-id-oldTerms-useTerms', path: 'useTerms', component: () => import('@/client/pages/mobile/user/[id]/cs/oldTerms/useTerms.vue'), children: [
                              { name: 'mobile-user-id-oldTerms-useTerms-250314', path: '250314', meta: { timeString: '2023년 12월  1일 ~ 2025년 3월 14일 적용' }, component: () => import('@/client/pages/mobile/user/[id]/cs/oldTerms/useTerms/250314.vue') }
                            ]
                          },
                          {
                            name: 'mobile-user-id-oldTerms-personTerms', path: 'personTerms', component: () => import('@/client/pages/mobile/user/[id]/cs/oldTerms/personTerms.vue'), children: [
                              { name: 'mobile-user-id-oldTerms-personTerms-250314', path: '250314', meta: { timeString: '2023년 12월  1일 ~ 2025년 3월 14일 적용' }, component: () => import('@/client/pages/mobile/user/[id]/cs/oldTerms/personTerms/250314.vue') }
                            ]
                          },
                          {
                            name: 'mobile-user-id-oldTerms-locationTerms', path: 'locationTerms', component: () => import('@/client/pages/mobile/user/[id]/cs/oldTerms/locationTerms.vue'), children: [

                            ]
                          }
                        ]
                      },
                      { name: 'mobile-user-id-openSource', path: 'openSource', component: () => import('@/client/pages/mobile/user/[id]/cs/openSource.vue') },
                      {
                        name: 'mobile-user-id-guide', path: 'guide', children: [
                          { name: 'mobile-user-id-guide-index', path: 'index', component: () => import('@/client/pages/mobile/user/[id]/cs/guide/index.vue') },
                          // owner
                          { name: 'mobile-user-id-guide-owner-1_serviceOverview', path: 'owner/1_serviceOverview', component: () => import('@/client/pages/mobile/user/[id]/cs/guide/owner/1_serviceOverview.vue') },
                          { name: 'mobile-user-id-guide-owner-2_status', path: 'owner/2_status', component: () => import('@/client/pages/mobile/user/[id]/cs/guide/owner/2_status.vue') },
                          { name: 'mobile-user-id-guide-owner-3_monitoring', path: 'owner/3_monitoring', component: () => import('@/client/pages/mobile/user/[id]/cs/guide/owner/3_monitoring.vue') },
                          { name: 'mobile-user-id-guide-owner-4_equipInfo', path: 'owner/4_equipInfo', component: () => import('@/client/pages/mobile/user/[id]/cs/guide/owner/4_equipInfo.vue') },
                          { name: 'mobile-user-id-guide-owner-5_powerGeneration', path: 'owner/5_powerGeneration', component: () => import('@/client/pages/mobile/user/[id]/cs/guide/owner/5_powerGeneration.vue') },
                          { name: 'mobile-user-id-guide-owner-6_report', path: 'owner/6_report', component: () => import('@/client/pages/mobile/user/[id]/cs/guide/owner/6_report.vue') },
                          { name: 'mobile-user-id-guide-owner-7_event', path: 'owner/7_event', component: () => import('@/client/pages/mobile/user/[id]/cs/guide/owner/7_event.vue') },
                          { name: 'mobile-user-id-guide-owner-8_ownerInfo', path: 'owner/8_ownerInfo', component: () => import('@/client/pages/mobile/user/[id]/cs/guide/owner/8_ownerInfo.vue') },
                          { name: 'mobile-user-id-guide-owner-9_question', path: 'owner/9_question', component: () => import('@/client/pages/mobile/user/[id]/cs/guide/owner/9_question.vue') },
                          // company
                          { name: 'mobile-user-id-guide-company-1_serviceOverview', path: 'company/1_serviceOverview', component: () => import('@/client/pages/mobile/user/[id]/cs/guide/company/1_serviceOverview.vue') },
                          { name: 'mobile-user-id-guide-company-2_status', path: 'company/2_status', component: () => import('@/client/pages/mobile/user/[id]/cs/guide/company/2_status.vue') },
                          { name: 'mobile-user-id-guide-company-3_equipSelect', path: 'company/3_equipSelect', component: () => import('@/client/pages/mobile/user/[id]/cs/guide/company/3_equipSelect.vue') },
                          { name: 'mobile-user-id-guide-company-4_instru', path: 'company/4_instru', component: () => import('@/client/pages/mobile/user/[id]/cs/guide/company/4_instru.vue') },
                          { name: 'mobile-user-id-guide-company-5_equipInfo', path: 'company/5_equipInfo', component: () => import('@/client/pages/mobile/user/[id]/cs/guide/company/5_equipInfo.vue') },
                          { name: 'mobile-user-id-guide-company-6_event', path: 'company/6_event', component: () => import('@/client/pages/mobile/user/[id]/cs/guide/company/6_event.vue') },
                          { name: 'mobile-user-id-guide-company-7_powerGeneration', path: 'company/7_powerGeneration', component: () => import('@/client/pages/mobile/user/[id]/cs/guide/company/7_powerGeneration.vue') },
                          { name: 'mobile-user-id-guide-company-8_installInfo', path: 'company/8_installInfo', component: () => import('@/client/pages/mobile/user/[id]/cs/guide/company/8_installInfo.vue') },
                          { name: 'mobile-user-id-guide-company-9_ownerInfo', path: 'company/9_ownerInfo', component: () => import('@/client/pages/mobile/user/[id]/cs/guide/company/9_ownerInfo.vue') },
                          { name: 'mobile-user-id-guide-company-10_question', path: 'company/10_question', component: () => import('@/client/pages/mobile/user/[id]/cs/guide/company/10_question.vue') },
                        ]
                      },
                    ]
                  },
                  {
                    name: 'mobile-user-id-info', path: 'info', meta: { name: '회원정보', layout: 'non-header-footer' }, component: () => import('@/client/pages/mobile/user/[id]/info.vue'), children: [
                      { name: 'mobile-user-id-info-main', path: 'main', meta: { name: '회원정보', layout: 'non-header-footer' }, component: () => import('@/client/pages/mobile/user/[id]/info/main.vue') },
                      { name: 'mobile-user-id-info-pw-change', path: 'pw-change', meta: { name: '비밀번호 변경' }, component: () => import('@/client/pages/mobile/user/[id]/info/pw-change.vue') },
                      { name: 'mobile-user-id-info-contact-change', path: 'contact-change', meta: { name: '연락처 변경' }, component: () => import('@/client/pages/mobile/user/[id]/info/contact-change.vue') },
                      { name: 'mobile-user-id-info-user-delete', path: 'user-delete', meta: { name: '회원 탈퇴' }, component: () => import('@/client/pages/mobile/user/[id]/info/user-delete.vue') },
                    ]
                  },
                  { name: 'mobile-user-id-setting', path: 'setting', meta: { name: '설정', layout: 'non-header-footer' }, component: () => import('@/client/pages/mobile/user/[id]/setting.vue') }
                ]
              },
              {
                name: 'mobile-user-finduser', path: 'finduser', component: () => import(`@/client/pages/mobile/user/finduser.vue`), children: [
                  { name: 'mobile-user-finduser-findid', path: 'findid', component: () => import(`@/client/pages/mobile/user/finduser/findid.vue`) },
                  { name: 'mobile-user-finduser-findpass', path: 'findpass', component: () => import(`@/client/pages/mobile/user/finduser/findpass.vue`) },
                  { name: 'mobile-user-finduser-doneFindid', path: 'doneFindid', component: () => import(`@/client/pages/mobile/user/finduser/doneFindid.vue`) },
                  { name: 'mobile-user-finduser-doneFindpass', path: 'doneFindpass', component: () => import(`@/client/pages/mobile/user/finduser/doneFindpass.vue`) },
                ]
              },
              {
                name: 'signin', path: 'signin', component: () => import(`@/client/pages/mobile/user/signin.vue`), children: [

                ]
              },
              { name: 'intro', path: 'intro', component: () => import(`@/client/pages/mobile/user/intro.vue`) },
              { name: 'logout', path: 'logout', component: () => import(`@/client/pages/mobile/user/logout.vue`) },
              {
                name: 'mobile-user-signin', path: 'signin', component: () => import('@/client/pages/mobile/user/signin.vue'), children: [
                  { name: 'mobile-user-signin-term', path: 'term', component: () => import('@/client/pages/mobile/user/signin/term.vue') },
                  { name: 'mobile-user-signin-useTerms', path: 'useTerms', component: () => import('@/client/pages/mobile/user/signin/useTerms.vue') },
                  { name: 'mobile-user-signin-personTerms', path: 'personTerms', component: () => import('@/client/pages/mobile/user/signin/personTerms.vue') }
                ]
              },
              { name: 'mobile-user-login', path: 'login', component: () => import(`@/client/pages/mobile/user/login.vue`), meta: { auth: false, layout: "non-header-footer", middleware: ['set-intro'] } },
            ]
          },
          {
            name: 'mobile-installer', path: 'installer', meta: { layout: 'non-header-footer', titleName: '현장설치업무' }, component: () => import('@/client/pages/mobile/installer.vue'), children: [

              {
                name: 'mobile-installer-policy', path: 'policy', meta: { titleName: '현장설치', barName: '작업 전 숙지사항' }, component: () => import('@/client/pages/mobile/installer/policy/index.vue'), children: [
                  { name: 'mobile-installer-policy-allpolicy', path: 'all', meta: { titleName: '현장설치' }, component: () => import('@/client/pages/mobile/installer/policy/all-policy.vue') },
                  { name: 'mobile-installer-policy-rtuinstall', path: 'rtu-install', meta: { titleName: '현장설치' }, component: () => import('@/client/pages/mobile/installer/policy/install-policy.vue') }
                ]
              },
              {
                name: 'mobile-installer-ownerwork', path: 'owners', meta: { titleName: '현장설치업무', barName: '설치자/업무선택' }, component: () => import('@/client/pages/mobile/installer/ownerwork/index.vue'), children: [
                  { name: 'mobile-installer-ownerwork-list', path: 'list', meta: { titleName: '현장설치업무' }, component: () => import('@/client/pages/mobile/installer/ownerwork/ownerlist.vue') },
                  { name: 'mobile-installer-ownerwork-history-list', path: 'history-list', meta: { titleName: '현장설치내역' }, component: () => import('@/client/pages/mobile/installer/ownerwork/historylist.vue') },
                  { name: 'mobile-installer-ownerwork-checkowner', path: 'checkowner', meta: { title: '현장설치업무' }, component: () => import('@/client/pages/mobile/installer/ownerwork/check-owner.vue') },
                  { name: 'mobile-installer-ownerwork-checkownerformer', path: 'checkowner-former', meta: { title: '현장설치업무' }, component: () => import('@/client/pages/mobile/installer/ownerwork/check-owner-former.vue') },
                  { name: 'mobile-installer-ownerwork-checkownermap', path: 'checkowner-map', meta: { title: '현장설치업무' }, component: () => import('@/client/pages/mobile/installer/ownerwork/check-owner-map.vue') },
                  { name: 'mobile-installer-ownerwork-checkownermapkakao', path: 'checkowner-map-kakao', meta: { title: '현장설치업무' }, component: () => import('@/client/pages/mobile/installer/ownerwork/check-owner-map-kakao.vue') }

                ]
              },
              {
                name: 'mobile-installer-rtuinstall', path: 'rtu-install', meta: { titleName: '현장설치', barName: 'RTU 연결' }, component: () => import('@/client/pages/mobile/installer/rtu-install/index.vue'), children: [
                  { name: 'mobile-installer-rtuinstall-install', path: 'install-connect', meta: { titleName: '현장설치' }, component: () => import('@/client/pages/mobile/installer/rtu-install/install.vue') },
                  { name: 'mobile-installer-rtuinstall-uninstall', path: 'uninstall-connect', meta: { titleName: 'RTU 연결해제' }, component: () => import('@/client/pages/mobile/installer/rtu-install/uninstall.vue') },
                  { name: 'mobile-installer-rtuinstall-qrreader', path: 'rtu-qr', meta: { titleName: '현장설치' }, component: () => import('@/client/pages/mobile/installer/rtu-install/qr-reader.vue') },
                ]
              },
              {
                name: 'mobile-installer-rtustatuscheck', path: 'rtu-status', meta: { titleName: '현장설치', barName: 'RTU 연결상태확인' }, component: () => import('@/client/pages/mobile/installer/rtu-connect/index.vue'), children: [
                  { name: 'mobile-installer-rtustatuscheck-oncheck', path: 'rtu-on-check', meta: { titleName: '현장설치' }, component: () => import('@/client/pages/mobile/installer/rtu-connect/rtu-ison.vue') },
                  { name: 'mobile-installer-rtustatuscheck-connect', path: 'connect-check', meta: { titleName: '현장설치' }, component: () => import('@/client/pages/mobile/installer/rtu-connect/connect.vue') },
                  { name: 'mobile-installer-rtustatuscheck-rtucheck', path: 'rtu-check', meta: { titleName: '현장설치' }, component: () => import('@/client/pages/mobile/installer/rtu-connect/check.vue') },
                ]
              },
              {
                name: 'mobile-installer-rtuworkhistory', path: 'work-history', meta: { titleName: '현장설치', barName: '작업내역제출' }, component: () => import('@/client/pages/mobile/installer/workhistory/index.vue'), children: [
                  { name: 'mobile-installer-rtuworkhistory-workinfo', path: 'work-info', meta: { titleName: '현장설치' }, component: () => import('@/client/pages/mobile/installer/workhistory/workinfo.vue') }
                ]
              },
            ]
          },
          { name: 'mobile-install-guide', path: 'install/guide', meta: { layout: 'non-header-footer', titleName: '현장설치' }, component: () => import("@/client/pages/mobile/install-guide.vue") },
          { name: 'usage-policy', path: 'usage-policy', component: () => import(`@/client/pages/mobile/usage-policy.vue`) },
          { name: 'privacy-policy', path: 'privacy-policy', meta: { layout: 'non-header-footer' }, component: () => import(`@/client/pages/mobile/privacy-policy.vue`) },
          {
            name: 'privacy-policy-old', path: 'privacy-policy-old', meta: { layout: 'non-header-footer' }, component: () => import(`@/client/pages/mobile/old-privacy-policy.vue`), children: [
              { name: 'privacy-policy-old-id', path: ':id', meta: { layout: 'non-header-footer' }, component: () => import(`@/client/pages/mobile/old-privacy-policy/250208.vue`) }
            ]
          },
          { name: 'none-contents-page', path: 'none-contents-page', component: () => import(`@/client/pages/mobile/none-contents-page.vue`) },
          { name: 'error-page', path: 'error-page', meta: { layout: 'non-header-footer' }, component: () => import(`@/client/pages/mobile/error-page.vue`) }
        ]
      };
    };


    return newBaseRoute();
  }
}

